/**
 * Asset Tree Service
 * 
 * @swagger-tag AssetTree
 * @description Controller for asset tree operations.
 */
import ApiService from './api';
import type { components } from '@/core/types/generated/api';
type AssetTreePathDto = components['schemas']['AssetTreePathDto'];
type AssetTreeNodeDtoPaginatedResponse = components['schemas']['AssetTreeNodeDtoPaginatedResponse'];

export interface GetChildrenParams {
    parentId?: string | null;
    position?: number;
    search?: string;
    // Back-compat alias for search
    text?: string;
    filters?: string;
    sorts?: string;
    // Include inactive assets in results when true
    includeInactive?: boolean;
    page?: number;
    pageSize?: number;
}

export interface SearchPathsParams {
    search: string;
    position?: number;
    limit?: number;
    // Include inactive assets in search results when true
    includeInactive?: boolean;
}

export interface GetAllParams {
    includeInactive?: boolean;
    position?: number;
}

export interface SearchParams {
    search: string;
    page?: number;
    pageSize?: number;
    position?: number;
    includeInactive?: boolean;
    maintainableOnly?: boolean;
    assetTypeSearch?: string;
}

export interface SearchPaginationDto {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
}

export interface AssetTreeSearchResult {
    data: AssetTreeNodeDto[];
    ancestors: AssetTreeNodeDto[];
    pagination: SearchPaginationDto;
}

type AssetTreeNodeDto = components['schemas']['AssetTreeNodeDto'];

export const AssetTreeService = {
    async getChildren(params: GetChildrenParams = {}): Promise<AssetTreeNodeDtoPaginatedResponse> {
    const query: Record<string, unknown> = {};
    if (params.parentId) query.parentId = params.parentId;
    if (params.position !== undefined) query.position = params.position;
    if (params.search || params.text) query.search = params.search ?? params.text;
    if (params.filters) query.filters = params.filters;
    if (params.sorts) query.sorts = params.sorts;
    if (params.includeInactive !== undefined) query.includeInactive = params.includeInactive;
    if (params.page !== undefined) query.page = params.page;
    if (params.pageSize !== undefined) query.pageSize = params.pageSize;
    const { data } = await ApiService.get<AssetTreeNodeDtoPaginatedResponse>('/assets/tree/children', { params: query });
        return data;
    },
    async searchPaths(params: SearchPathsParams): Promise<AssetTreePathDto[]> {
    const query: Record<string, unknown> = {};
    if (params.search) query.search = params.search;
    if (params.position !== undefined) query.position = params.position;
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.includeInactive !== undefined) query.includeInactive = params.includeInactive;
    const { data } = await ApiService.get<AssetTreePathDto[]>('/assets/tree/search-paths', { params: query });
        return data || [];
    },
    async getAll(params: GetAllParams = {}): Promise<AssetTreeNodeDto[]> {
        const query: Record<string, unknown> = {};
        if (params.includeInactive !== undefined) query.includeInactive = params.includeInactive;
        if (params.position !== undefined) query.position = params.position;
        const { data } = await ApiService.get<AssetTreeNodeDto[]>('/assets/tree/all', { params: query });
        return data || [];
    },
    async search(params: SearchParams): Promise<AssetTreeSearchResult> {
        const query: Record<string, unknown> = { search: params.search };
        if (params.page !== undefined) query.page = params.page;
        if (params.pageSize !== undefined) query.pageSize = params.pageSize;
        if (params.position !== undefined) query.position = params.position;
        if (params.includeInactive !== undefined) query.includeInactive = params.includeInactive;
        if (params.maintainableOnly !== undefined) query.maintainableOnly = params.maintainableOnly;
        if (params.assetTypeSearch !== undefined && params.assetTypeSearch !== '') query.assetTypeSearch = params.assetTypeSearch;
        const { data } = await ApiService.get<AssetTreeSearchResult>('/assets/tree/search', { params: query });
        return data;
    }
};
